import { TrackGroupGetResponse, Ulid } from "@kase/my-marathon-communication";
import { signal } from "@preact/signals";
import { store } from "~/core/store";
import { ApiSliceParams } from "../interfaces";
import { createListController } from "../utils";
import {
    CreateTrackGroupRequest,
    TrackGroup,
    UpdateTrackGroupRequest,
} from "./interfaces";

export const makeGroup = ({ connection, company }: ApiSliceParams) => {
    const view = async (group: TrackGroupGetResponse) => {
        const {
            project: projectApi,
            feeCode: feeCodeApi,
            feeCategory: feeCategoryApi,
        } = store.api;
        const project = await projectApi.getOrLoad(
            group.project,
            group.company,
        );
        const feeCode = group.fee_code
            ? await feeCodeApi.getOrLoad(group.fee_code)
            : null;
        const feeCategory = group.lob
            ? await feeCategoryApi.getOrLoad(group.lob)
            : null;
        const itag = signal(0);

        return {
            ...group,
            id: group.id.toString(),
            _id: group.id,
            _project: group.project,
            project,
            feeCode,
            feeCategory,
            itag,
        };
    };

    const {
        items: trackGroups,
        allLoaded: loaded,
        allLoading: loading,
        listen,
        whenLoaded,
        getOrLoad,
    } = createListController<TrackGroupGetResponse, TrackGroup>({
        name: "trackgroup",
        connection,
        list: (con) => con.time.trackgroup.items(),
        view,
    });

    const importTemplates = async () => {
        const con = connection.value;
        const { feeCode: feeCodeApi, project: projectApi } = store.api;
        const res = con.time.template.list({ company: company.value });
        const isLoB = Boolean(con.user.lob);
        const source: {
            project: Readonly<Ulid>;
            feeCode: Readonly<Ulid> | null;
            lob: Readonly<Ulid> | null;
            name: string;
        }[] = [];

        const getName = (
            projectId: Readonly<Ulid>,
            feeCodeId: Readonly<Ulid> | null,
            description: string | undefined,
        ) => {
            const project = projectApi.get(projectId.toString());
            const feeCode = feeCodeId
                ? feeCodeApi.get(feeCodeId.toString())
                : null;

            if (feeCode && description) {
                return `${feeCode.name_global} - ${description}`;
            } else if (feeCode) {
                return feeCode.name_global;
            } else if (description) {
                return description;
            } else if (project) {
                return project.name;
            }

            return "Imported from template";
        };

        for await (const id of res) {
            for await (const item of con.time.template.get({
                id,
                company: company.value,
            })) {
                const project = item.project;
                const feeCode =
                    item.line_type?.$case === "fee"
                        ? (item.line_type.fee.feecode ?? null)
                        : null;
                const lob =
                    item.line_type?.$case === "fee"
                        ? (item.line_type.fee.lob ?? null)
                        : null;
                const name = getName(project, feeCode, item.description);
                const projectEntity = projectApi.get(project.toString());

                if (projectEntity && projectEntity.active) {
                    source.push({
                        project,
                        feeCode:
                            feeCode && Ulid.ZERO.equals(feeCode)
                                ? null
                                : feeCode,
                        lob: lob && Ulid.ZERO.equals(lob) ? null : lob,
                        name,
                    });
                }
            }
        }

        const groups = trackGroups.peek();
        const finalSource = source.filter(
            (i) =>
                !groups.find(
                    (g) =>
                        g._project.equals(i.project) &&
                        (i.feeCode
                            ? i.feeCode && g.fee_code?.equals(i.feeCode)
                            : true) &&
                        (isLoB && i.lob ? g.lob && g.lob.equals(i.lob) : true),
                ),
        );
        const promises: Promise<unknown>[] = [];

        for (const item of finalSource) {
            promises.push(
                create({
                    project: item.project,
                    feeCode: item.feeCode,
                    lob: item.lob,
                    name: item.name,
                    template: true,
                }),
            );
        }

        const result = await Promise.allSettled(promises);
        const successCount = result.filter(
            (i) => i.status === "fulfilled",
        ).length;
        const errorCount = result.filter((i) => i.status === "rejected").length;
        const ignoreCount = source.length - finalSource.length;

        return {
            successCount,
            errorCount,
            ignoreCount,
        };
    };

    const create = async (request: CreateTrackGroupRequest) => {
        const con = connection.value;
        const isLob = Boolean(con.user.lob);
        const finalLob = isLob ? (request.lob ?? undefined) : undefined;

        await con.time.trackgroup.create({
            name: request.name,
            project: request.project,
            fee_code: request.feeCode ?? undefined,
            lob: finalLob,
            company: company.value,
            comment: request.comment,
            fill_out: request.fillOut,
            template: request.template,
        });
    };

    const update = async (request: UpdateTrackGroupRequest) => {
        const con = connection.value;
        const tg = trackGroups.peek().find((i) => i._id.equals(request.id));
        const isLob = Boolean(con.user.lob);

        await con.time.trackgroup.update({
            id: request.id,
            name: request.name,
            project:
                request.project && tg?._project.equals(request.project)
                    ? undefined
                    : request.project
                      ? request.project
                      : undefined,
            fee_code:
                request.feeCode === null
                    ? Ulid.ZERO
                    : request.feeCode?.equals(tg?.fee_code ?? Ulid.ZERO)
                      ? undefined
                      : request.feeCode,
            lob: isLob
                ? request.lob === null
                    ? Ulid.ZERO
                    : request.lob?.equals(tg?.lob ?? Ulid.ZERO)
                      ? undefined
                      : request.lob
                : undefined,
            comment: request.comment,
            active: request.active,
            fill_out: request.fillOut,
            template: request.template,
        });
    };

    const remove = async (id: Readonly<Ulid>) => {
        const con = connection.value;

        await con.time.trackgroup.delete({ id });
    };

    return {
        trackGroups,
        loading,
        loaded,
        listen,
        whenLoaded,
        getOrLoad,
        importTemplates,
        create,
        remove,
        update,
    };
};
